import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Tour step definitions
const TOUR_STEPS = [
  {
    id: "welcome",
    type: "modal",
    title: "Welcome to Foundry!",
    description: "Let's walk through how to validate your startup idea. This quick tour will show you the key features.",
  },
  {
    id: "dashboard",
    type: "highlight",
    target: "[data-tour-target='sidebar-dashboard']",
    placement: "right",
    title: "Dashboard",
    content: "Your home base. See metrics, activity, and an overview of your workspace at a glance.",
    page: "/dashboard",
  },
  {
    id: "business-models",
    type: "highlight",
    target: "[data-tour-target='sidebar-business-models']",
    placement: "right",
    title: "Start with a Canvas",
    content: "Create a Lean Canvas or Business Model Canvas to map out your business model.",
    page: "/dashboard",
  },
  {
    id: "hypotheses",
    type: "highlight",
    target: "[data-tour-target='sidebar-discoveries']",
    placement: "right",
    title: "Validate with Discoveries",
    content: "Upload interview transcripts, research documents, and evidence to test your assumptions.",
    page: "/dashboard",
  },
  {
    id: "journey-maps",
    type: "highlight",
    target: "[data-tour-target='sidebar-journey-maps']",
    placement: "right",
    title: "Map the Customer Journey",
    content: "Visualize your customer's experience across stages — from awareness to advocacy.",
    page: "/dashboard",
  },
  {
    id: "roadmaps",
    type: "highlight",
    target: "[data-tour-target='sidebar-roadmaps']",
    placement: "right",
    title: "Plan Your Roadmap",
    content: "Organize what to build Now, Next, and Later based on your validated learnings.",
    page: "/dashboard",
  },
  {
    id: "tasks",
    type: "highlight",
    target: "[data-tour-target='sidebar-tasks']",
    placement: "right",
    title: "Track Tasks",
    content: "Break roadmap items into actionable tasks and track progress on a kanban board.",
    page: "/dashboard",
  },
  {
    id: "wait-list",
    type: "highlight",
    target: "[data-tour-target='sidebar-wait-list']",
    placement: "right",
    title: "Collect Interest",
    content: "Build a wait list to capture early adopters and gauge demand before you launch.",
    page: "/dashboard",
  },
  {
    id: "claude-banner",
    type: "highlight",
    target: "#dashboard-claude-banner",
    placement: "bottom",
    title: "Your AI Co-Founder",
    content: "Foundry brings founders, teams, and AI together in one workspace. Connect Claude Desktop to collaborate from a single source of truth as you work toward product-market fit.",
    page: "/dashboard",
    optional: true,  // skip if element not found (banner may be dismissed)
  },
  {
    id: "users",
    type: "highlight",
    target: "[data-tour-target='sidebar-users']",
    placement: "right",
    title: "Invite Your Team",
    content: "Add teammates to collaborate on canvases, roadmaps, and tasks together.",
    page: "/dashboard",
  },
  {
    id: "completion",
    type: "modal",
    title: "You're all set!",
    description: "You now know the key features of Foundry. Start by creating your first canvas — or connect Claude Desktop with the Foundry Co-Founder Plugin to build everything through conversation.",
  },
]

const TOUR_CONFIG = {
  canEndTour: true,
  buttons: {
    next: "Next",
    back: "Back",
    done: "Finish",
    end: "Skip tour",
  },
}

export default class extends Controller {
  static values = {
    completed: Boolean,
    step: Number,
  }

  connect() {
    if (this.completedValue) return

    this.startTourFromStep(this.stepValue || 0)
  }

  disconnect() {
    this.dismissModal()
    if (window.Tourist && window.Tourist.destroy) {
      window.Tourist.destroy()
    }
  }

  startTourFromStep(stepIndex) {
    const step = TOUR_STEPS[stepIndex]
    if (!step) return

    // Clean up any active Tourist instance before starting a new step
    if (window.Tourist && window.Tourist.destroy) {
      window.Tourist.destroy()
    }

    if (step.type === "modal") {
      this.showModal(step, stepIndex)
    } else {
      this.showHighlightSteps(stepIndex)
    }
  }

  // ---- Modal steps ----

  showModal(step, stepIndex) {
    this.dismissModal()

    const isCompletion = stepIndex === TOUR_STEPS.length - 1

    const overlay = document.createElement("div")
    overlay.id = "onboarding-tour-modal-overlay"
    overlay.style.cssText = "position:fixed;inset:0;background:rgba(0,0,0,0.5);z-index:999998;display:flex;align-items:center;justify-content:center;"

    const modal = document.createElement("div")
    modal.style.cssText = `background:var(--tourist-bg, #fff);border-radius:0.75rem;padding:2rem;max-width:${isCompletion ? '34rem' : '28rem'};width:90%;text-align:center;color:var(--tourist-text, #1f2937);box-shadow:0 25px 50px -12px rgba(0,0,0,0.25);`

    const completionButtons = isCompletion
      ? `<button id="tour-modal-skip" style="padding:0.5rem 1rem;font-size:0.875rem;color:var(--tourist-text-secondary, #6b7280);background:none;border:1px solid var(--tourist-border, #d1d5db);border-radius:0.375rem;cursor:pointer;">I'll explore on my own</button>
         <button id="tour-modal-action" style="padding:0.5rem 1.5rem;font-size:0.875rem;font-weight:600;color:#fff;background:var(--tourist-primary, #2563eb);border:none;border-radius:0.375rem;cursor:pointer;">Create my first canvas</button>`
      : `<button id="tour-modal-skip" style="padding:0.5rem 1rem;font-size:0.875rem;color:var(--tourist-text-secondary, #6b7280);background:none;border:1px solid var(--tourist-border, #d1d5db);border-radius:0.375rem;cursor:pointer;">Skip tour</button>
         <button id="tour-modal-action" style="padding:0.5rem 1.5rem;font-size:0.875rem;font-weight:600;color:#fff;background:var(--tourist-primary, #2563eb);border:none;border-radius:0.375rem;cursor:pointer;">Start tour</button>`

    modal.innerHTML = `
      <h2 style="font-size:1.25rem;font-weight:700;margin-bottom:0.75rem;">${step.title}</h2>
      <p style="font-size:0.875rem;color:var(--tourist-text-secondary, #6b7280);margin-bottom:1.5rem;line-height:1.6;">${step.description}</p>
      <div style="display:flex;gap:0.75rem;justify-content:center;">
        ${completionButtons}
      </div>
    `

    overlay.appendChild(modal)
    document.body.appendChild(overlay)

    modal.querySelector("#tour-modal-action").addEventListener("click", async () => {
      this.dismissModal()
      if (isCompletion) {
        await this.completeTour()
        window.Turbo.visit("/workspace/canvases")
      } else {
        this.advanceToStep(stepIndex + 1)
      }
    })

    const skipBtn = modal.querySelector("#tour-modal-skip")
    if (skipBtn) {
      skipBtn.addEventListener("click", async () => {
        this.dismissModal()
        await this.completeTour()
      })
    }
  }

  dismissModal() {
    const existing = document.getElementById("onboarding-tour-modal-overlay")
    if (existing) existing.remove()
  }

  // ---- Highlight steps (Tourist) ----

  showHighlightSteps(fromIndex) {
    // Collect consecutive highlight steps on the current page
    const highlightSteps = []
    const stepIndices = []

    for (let i = fromIndex; i < TOUR_STEPS.length; i++) {
      const step = TOUR_STEPS[i]
      if (step.type === "modal") break

      const target = document.querySelector(step.target)
      if (!target && step.optional) continue
      if (!target) break

      highlightSteps.push({
        target: step.target,
        placement: step.placement || "right",
        title: step.title,
        content: step.content,
        offset: step.offset,
        highlight: step.highlight,
        blockElement: step.blockElement,
      })
      stepIndices.push(i)
    }

    if (highlightSteps.length === 0) {
      // No valid highlight steps, skip to next modal or end
      const nextModal = TOUR_STEPS.findIndex((s, i) => i > fromIndex && s.type === "modal")
      if (nextModal !== -1) {
        this.advanceToStep(nextModal)
      } else {
        this.completeTour()
      }
      return
    }

    // Ensure sidebar is open for sidebar-targeted steps
    this.ensureSidebarOpen()

    window.Tourist.show(highlightSteps, TOUR_CONFIG, {
      onEnd: () => {
        // Find the step after the last highlight step shown
        const lastShownIndex = stepIndices[stepIndices.length - 1]
        const nextIndex = lastShownIndex + 1
        this.advanceToStep(nextIndex)
      },
      onSkip: () => this.completeTour(),
    })
  }

  // ---- Navigation ----

  advanceToStep(stepIndex) {
    if (stepIndex >= TOUR_STEPS.length) {
      this.completeTour()
      return
    }

    this.stepValue = stepIndex
    this.persistStep(stepIndex)

    const step = TOUR_STEPS[stepIndex]

    // Check if we need to navigate to a different page
    if (step.page && !window.location.pathname.startsWith(step.page)) {
      window.Turbo.visit(step.page)
      return
    }

    this.startTourFromStep(stepIndex)
  }

  async completeTour() {
    this.completedValue = true

    await post("/account/update_tour_state", {
      body: JSON.stringify({ completed: true }),
      contentType: "application/json",
      responseKind: "json",
    })
  }

  persistStep(stepIndex) {
    post("/account/update_tour_state", {
      body: JSON.stringify({ step: stepIndex }),
      contentType: "application/json",
      responseKind: "json",
    })
  }

  // ---- Sidebar ----

  ensureSidebarOpen() {
    const sidebar = document.querySelector("[data-sidebar-target='sidebar']")
    if (sidebar && sidebar.classList.contains("-translate-x-full")) {
      const sidebarController = this.application.getControllerForElementAndIdentifier(
        document.body, "sidebar"
      )
      if (sidebarController) sidebarController.open()
    }
  }

  // ---- Restart (called from sidebar dropdown) ----

  async restartTour() {
    this.completedValue = false
    this.stepValue = 0

    await post("/account/restart_tour", {
      contentType: "application/json",
      responseKind: "json",
    })

    if (window.location.pathname === "/dashboard") {
      this.startTourFromStep(0)
    } else {
      window.Turbo.visit("/dashboard")
    }
  }
}
